import { useState, useRef, useCallback } from "react";
import { Share2, Download, X, Twitter, Facebook, Copy, Check } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import html2canvas from "html2canvas";

const CATEGORY_GRADIENTS = {
  retrograde: "linear-gradient(135deg, #1a0a0a 0%, #2d1010 50%, #1a0505 100%)",
  direct: "linear-gradient(135deg, #0a1a0a 0%, #102d10 50%, #051a05 100%)",
  ingress: "linear-gradient(135deg, #0a0a1a 0%, #10102d 50%, #05051a 100%)",
  new_moon: "linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 50%, #0d0d0d 100%)",
  full_moon: "linear-gradient(135deg, #1a1508 0%, #2d2510 50%, #1a1205 100%)",
  solar_eclipse: "linear-gradient(135deg, #150a1a 0%, #25102d 50%, #10051a 100%)",
  lunar_eclipse: "linear-gradient(135deg, #1a0508 0%, #2d0810 50%, #1a0305 100%)",
};

const CATEGORY_COLORS = {
  retrograde: "#EF4444",
  direct: "#22C55E",
  ingress: "#3B82F6",
  new_moon: "#9CA3AF",
  full_moon: "#FCD34D",
  solar_eclipse: "#A78BFA",
  lunar_eclipse: "#F87171",
};

const CATEGORY_LABELS = {
  retrograde: "Retrograde",
  direct: "Direct Station",
  ingress: "Sign Change",
  new_moon: "New Moon",
  full_moon: "Full Moon",
  solar_eclipse: "Solar Eclipse",
  lunar_eclipse: "Lunar Eclipse",
};

function formatShareDate(utcString) {
  const date = new Date(utcString);
  return date.toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

function formatShareTime(utcString) {
  const date = new Date(utcString);
  return date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  });
}

// Extract first 2-3 bullet points from interpretation for the card
function extractKeyPoints(interpretation) {
  if (!interpretation) return [];
  
  // Split by bullet points or newlines
  const lines = interpretation.split(/[•\n]/).filter(line => {
    const trimmed = line.trim();
    return trimmed.length > 10 && !trimmed.toLowerCase().includes("you might");
  });
  
  // Take first 3 meaningful points
  return lines.slice(0, 3).map(line => {
    // Clean up and truncate if needed
    let cleaned = line.trim().replace(/^[-–—]\s*/, "");
    if (cleaned.length > 60) {
      cleaned = cleaned.substring(0, 57) + "...";
    }
    return cleaned;
  });
}

export default function ShareEventCard({ event, onClose }) {
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedImage, setGeneratedImage] = useState(null);
  const [copied, setCopied] = useState(false);
  const cardRef = useRef(null);

  const gradient = CATEGORY_GRADIENTS[event.category] || CATEGORY_GRADIENTS.ingress;
  const accentColor = CATEGORY_COLORS[event.category] || "#3B82F6";
  const categoryLabel = CATEGORY_LABELS[event.category] || "Celestial Event";
  const keyPoints = extractKeyPoints(event.interpretation);

  const generateImage = useCallback(async () => {
    if (!cardRef.current) return null;
    
    setIsGenerating(true);
    try {
      const canvas = await html2canvas(cardRef.current, {
        backgroundColor: null,
        scale: 2, // Higher quality
        useCORS: true,
        logging: false,
      });
      
      const dataUrl = canvas.toDataURL("image/png");
      setGeneratedImage(dataUrl);
      return dataUrl;
    } catch (err) {
      console.error("Failed to generate image:", err);
      return null;
    } finally {
      setIsGenerating(false);
    }
  }, []);

  const handleDownload = async () => {
    const imageUrl = generatedImage || await generateImage();
    if (!imageUrl) return;

    const link = document.createElement("a");
    link.download = `${event.title.replace(/\s+/g, "-").toLowerCase()}-dailynakshatra.png`;
    link.href = imageUrl;
    link.click();
  };

  const handleCopyImage = async () => {
    const imageUrl = generatedImage || await generateImage();
    if (!imageUrl) return;

    try {
      // Convert data URL to blob
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      
      await navigator.clipboard.write([
        new ClipboardItem({ "image/png": blob })
      ]);
      
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy image:", err);
      // Fallback: copy the share text
      const shareText = `${event.title} - ${formatShareDate(event.timestamp_utc)}\n\n${event.interpretation?.substring(0, 200)}...\n\nvia Daily Nakshatra`;
      await navigator.clipboard.writeText(shareText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleShareTwitter = async () => {
    const text = `${event.icon} ${event.title}\n${formatShareDate(event.timestamp_utc)}\n\n${keyPoints[0] || ""}\n\n`;
    const url = "https://dailynakshatra.com";
    window.open(
      `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`,
      "_blank"
    );
  };

  const handleShareFacebook = () => {
    const url = "https://dailynakshatra.com";
    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      "_blank"
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="w-full max-w-lg rounded-xl overflow-hidden"
        style={{ background: "#111" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
          <h3 className="text-white/90 font-medium flex items-center gap-2">
            <Share2 className="w-4 h-4" />
            Share Event
          </h3>
          <button
            onClick={onClose}
            className="text-white/50 hover:text-white/80 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Preview Card */}
        <div className="p-4">
          <div className="rounded-lg overflow-hidden shadow-2xl">
            {/* The actual card that will be rendered to image */}
            <div
              ref={cardRef}
              className="w-full aspect-[4/5] p-6 flex flex-col"
              style={{ background: gradient }}
            >
              {/* Top section with branding */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2">
                  <img src="/logo.png" alt="" className="w-6 h-6" />
                  <span className="text-white/60 text-xs tracking-wider uppercase">Daily Nakshatra</span>
                </div>
                <span
                  className="text-[10px] tracking-wider uppercase px-2 py-1 rounded"
                  style={{ background: `${accentColor}30`, color: accentColor }}
                >
                  {categoryLabel}
                </span>
              </div>

              {/* Main content */}
              <div className="flex-1 flex flex-col justify-center">
                {/* Icon */}
                <div
                  className="w-16 h-16 rounded-full flex items-center justify-center text-3xl mb-4 mx-auto"
                  style={{ background: `${accentColor}20`, boxShadow: `0 0 40px ${accentColor}30` }}
                >
                  {event.icon}
                </div>

                {/* Title */}
                <h2 className="text-white text-2xl font-semibold text-center mb-2">
                  {event.title}
                </h2>

                {/* Subtitle */}
                {event.subtitle && (
                  <p className="text-white/50 text-sm text-center mb-4">{event.subtitle}</p>
                )}

                {/* Date & Time */}
                <div className="text-center mb-6">
                  <p className="text-white/80 text-sm font-medium">
                    {formatShareDate(event.timestamp_utc)}
                  </p>
                  <p className="text-white/40 text-xs">
                    {formatShareTime(event.timestamp_utc)}
                  </p>
                </div>

                {/* Key Points */}
                {keyPoints.length > 0 && (
                  <div className="space-y-2 px-2">
                    {keyPoints.map((point, idx) => (
                      <div
                        key={idx}
                        className="flex items-start gap-2 text-white/60 text-xs"
                      >
                        <span style={{ color: accentColor }}>•</span>
                        <span className="leading-relaxed">{point}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Bottom branding */}
              <div className="mt-auto pt-4 border-t border-white/10">
                <p className="text-white/30 text-[10px] text-center tracking-wider">
                  TROPICAL ZODIAC
                </p>
                <p className="text-white/50 text-xs text-center mt-1">
                  dailynakshatra.com
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Share Actions */}
        <div className="px-4 pb-4 space-y-3">
          {/* Primary actions */}
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={handleDownload}
              disabled={isGenerating}
              className="flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-white/10 hover:bg-white/15 text-white/90 text-sm font-medium transition-colors disabled:opacity-50"
              data-testid="share-download-btn"
            >
              <Download className="w-4 h-4" />
              {isGenerating ? "Generating..." : "Download"}
            </button>
            <button
              onClick={handleCopyImage}
              disabled={isGenerating}
              className="flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-white/10 hover:bg-white/15 text-white/90 text-sm font-medium transition-colors disabled:opacity-50"
              data-testid="share-copy-btn"
            >
              {copied ? (
                <>
                  <Check className="w-4 h-4 text-green-400" />
                  Copied!
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4" />
                  Copy Image
                </>
              )}
            </button>
          </div>

          {/* Social share buttons */}
          <div className="flex items-center justify-center gap-3">
            <span className="text-white/30 text-xs">Share to:</span>
            <button
              onClick={handleShareTwitter}
              className="w-10 h-10 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-white/70 hover:text-white transition-colors"
              title="Share on X (Twitter)"
              data-testid="share-twitter-btn"
            >
              <Twitter className="w-4 h-4" />
            </button>
            <button
              onClick={handleShareFacebook}
              className="w-10 h-10 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-white/70 hover:text-white transition-colors"
              title="Share on Facebook"
              data-testid="share-facebook-btn"
            >
              <Facebook className="w-4 h-4" />
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}
